/**
 * Maskito options for manually typed MM/DD/YYYY dates, so a user never has to
 * type the '/' characters themselves.
 *
 * Entering "01021990" fills the slashes in, entering "01/02/1990" is accepted
 * as typed, and the mask rejects impossible segments (month 13, Feb 30, and so
 * on) as they are entered. Applied by components/datepicker.vue.
 *
 * @see https://maskito.dev/kit/date
 */
import { maskitoDate } from '@maskito/kit';

const SEPARATOR = '/';
const MODE = 'mm/dd/yyyy';

// Digit counts for MM / DD / YYYY.
const SEGMENT_SIZES = [2, 2, 4];

/**
 * Maskito drops a separator typed before its segment is full, because the
 * character does not fit the mask template yet. Left alone, "1/2/1990" ends up
 * as "12/19/90" -- a silently wrong date. Pad the open segment ("1" -> "01") so
 * the separator lands where the template expects it.
 */
function padSegmentOnSeparator({ elementState, data }) {
  if (data !== SEPARATOR) return { elementState, data };

  const { value, selection } = elementState;
  const [from, to] = selection;

  // Only when the caret sits at the end of what has been typed so far.
  if (from !== to || from !== value.length) return { elementState, data };

  const segments = value.split(SEPARATOR);
  const index = segments.length - 1;
  const segment = segments[index];
  const size = SEGMENT_SIZES[index];

  // Nothing typed yet, segment already full, or no separator follows it.
  if (!segment || !size || segment.length >= size || index >= SEGMENT_SIZES.length - 1) {
    return { elementState, data };
  }

  const padded = [
    ...segments.slice(0, index),
    segment.padStart(size, '0'),
  ].join(SEPARATOR);

  return {
    elementState: { value: padded, selection: [padded.length, padded.length] },
    data,
  };
}

const dateMaskOptions = maskitoDate({ mode: MODE, separator: SEPARATOR });

export default {
  ...dateMaskOptions,
  preprocessors: [padSegmentOnSeparator, ...dateMaskOptions.preprocessors],
};
